// ── Client-side $TOKEN (SPL) fee payment. ──
// The player signs + sends ONE SPL transfer (their wallet → the treasury) for every on-chain
// action (mint a beast, pay for a card / premium item in $TOKEN), then posts the resulting
// signature to the API, which fetches + validates it server-side. The client NEVER holds a
// treasury key and never mutates balances — it only moves its own tokens and reports the sig.
//
// Heavy deps (@solana/web3.js, @solana/spl-token) are LAZY-imported the first time a payment is
// actually attempted, so a normal play session that never touches on-chain pays no bundle/runtime
// cost and the feature degrades cleanly (every failure → null, never throws) when:
//   • no injected wallet can sign + send a transaction,
//   • the token feature is unconfigured (no mint / treasury),
//   • the RPC / transfer fails or is rejected.

import {
  Connection,
  PublicKey,
  Transaction,
  SystemProgram,
} from '@solana/web3.js';
import {
  getAssociatedTokenAddress,
  createAssociatedTokenAccountInstruction,
  createTransferCheckedInstruction,
  TOKEN_2022_PROGRAM_ID,
} from '@solana/spl-token';
import type { TokenConfigResponse } from '@mn/shared';

/** A Phantom-style provider that can build + send a transaction (superset of the auth provider). */
interface TxProvider {
  publicKey?: { toString(): string } | null;
  /** Phantom: sign + submit in one step, returns the tx signature. */
  signAndSendTransaction?(tx: unknown): Promise<{ signature: string }>;
}

function txProvider(): TxProvider | null {
  if (typeof window === 'undefined') return null;
  const p = (window.phantom?.solana ?? window.solana) as TxProvider | undefined;
  return p && typeof p.signAndSendTransaction === 'function' && p.publicKey ? p : null;
}

/** True only when the injected wallet can sign+send a transaction (so the $TOKEN path is usable). */
export function canPayToken(): boolean {
  return txProvider() !== null;
}

/**
 * Prompt the injected wallet (Phantom etc.) to connect a signer, so {@link canPayToken} flips true.
 * Lets a player who is logged in but has no active signer connect it right at the moment of purchase.
 * Returns true once a sign-capable wallet is connected.
 */
export async function connectPayProvider(): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  const p = (window.phantom?.solana ?? window.solana) as
    | (TxProvider & { connect?: (opts?: unknown) => Promise<unknown> })
    | undefined;
  if (!p || typeof p.connect !== 'function') return false;
  try {
    await p.connect();
  } catch {
    return false; // user dismissed the wallet prompt
  }
  return canPayToken();
}

/** Convert a UI-unit token amount to base units (integer, BigInt-safe). */
function toBaseUnits(uiAmount: number, decimals: number): bigint {
  const dec = Math.max(0, Math.min(18, Math.floor(decimals)));
  // round to avoid float drift, then scale; build via string so large amounts stay exact
  const scaled = Math.round(Number(uiAmount) * 10 ** dec);
  return BigInt(scaled);
}

/**
 * Build, sign and send an SPL transfer of `uiAmount` $TOKEN from the connected wallet to the
 * treasury described by `cfg`. Resolves to the on-chain signature on success, or null on ANY
 * failure (no wallet, unconfigured, user rejected, RPC error) — callers treat null as "couldn't
 * pay" and surface a friendly message. Idempotent ATAs are created as needed.
 *
 * @param cfg the token config from GET /token/config (mint + treasury + decimals)
 * @param uiAmount the fee in UI units (the server re-derives + re-validates the true amount)
 * @param rpcUrl an RPC endpoint to submit through (the same one the API advertises)
 */
export async function payTokenToTreasury(
  cfg: Pick<TokenConfigResponse, 'enabled' | 'mint' | 'treasury' | 'decimals'>,
  uiAmount: number,
  rpcUrl: string,
): Promise<string | null> {
  const provider = txProvider();
  if (!provider || !cfg?.enabled || !cfg.mint || !cfg.treasury || !rpcUrl) return null;
  if (!(Number(uiAmount) > 0)) return null;

  try {
    const owner = new PublicKey(provider.publicKey!.toString());
    const mint = new PublicKey(cfg.mint);
    const treasury = new PublicKey(cfg.treasury);
    const decimals = cfg.decimals || 6;

    const connection = new Connection(rpcUrl, 'confirmed');

    // $TOKEN is a Token-2022 mint → ATAs + the transfer MUST be derived/built against the
    // Token-2022 program (classic SPL derivation yields the wrong account + a failing transfer).
    const fromAta = await getAssociatedTokenAddress(mint, owner, false, TOKEN_2022_PROGRAM_ID);
    const toAta = await getAssociatedTokenAddress(mint, treasury, true, TOKEN_2022_PROGRAM_ID);

    const tx = new Transaction();
    // Create the treasury ATA if it doesn't exist yet (idempotent; payer = the player).
    const toInfo = await connection.getAccountInfo(toAta);
    if (!toInfo) {
      tx.add(createAssociatedTokenAccountInstruction(owner, toAta, treasury, mint, TOKEN_2022_PROGRAM_ID));
    }
    tx.add(
      createTransferCheckedInstruction(
        fromAta,
        mint,
        toAta,
        owner,
        toBaseUnits(uiAmount, decimals),
        decimals,
        [],
        TOKEN_2022_PROGRAM_ID,
      ),
    );

    const { blockhash } = await connection.getLatestBlockhash('confirmed');
    tx.recentBlockhash = blockhash;
    tx.feePayer = owner;

    const { signature } = await provider.signAndSendTransaction(tx);
    // Wait for confirmation so the API can immediately fetch + validate the finalized tx.
    await connection.confirmTransaction(signature, 'confirmed');
    return signature;
  } catch (err) {
    console.warn('[token-pay] transfer failed — degrading to "could not pay"', err);
    return null;
  }
}

/**
 * Send a native SOL transfer (the connected wallet → treasury). Resolves to the on-chain signature
 * on success, or null on ANY failure. The server re-verifies the lamports received to the treasury.
 *
 * @param treasury the treasury wallet (destination)
 * @param sol the amount in SOL (UI units); the server re-derives + re-validates the true lamports
 * @param rpcUrl an RPC endpoint to submit through
 */
export async function paySolToTreasury(
  treasury: string,
  sol: number,
  rpcUrl: string,
): Promise<string | null> {
  const provider = txProvider();
  if (!provider || !treasury || !rpcUrl) return null;
  if (!(Number(sol) > 0)) return null;

  try {
    const owner = new PublicKey(provider.publicKey!.toString());
    const to = new PublicKey(treasury);
    const lamports = Math.round(Number(sol) * 1e9);

    const connection = new Connection(rpcUrl, 'confirmed');
    const tx = new Transaction().add(
      SystemProgram.transfer({ fromPubkey: owner, toPubkey: to, lamports }),
    );
    const { blockhash } = await connection.getLatestBlockhash('confirmed');
    tx.recentBlockhash = blockhash;
    tx.feePayer = owner;

    const { signature } = await provider.signAndSendTransaction(tx);
    await connection.confirmTransaction(signature, 'confirmed');
    return signature;
  } catch (err) {
    console.warn('[token-pay] SOL transfer failed — degrading to "could not pay"', err);
    return null;
  }
}
